import {
  ScheduledContentStatus,
  useGetPublishedScheduledContentTimestampsQuery,
  useGetPublishedScheduledContentWindowQuery,
  useGetScheduledContentByAllessehIdQuery,
  useInfiniteGetLastNPublishedScheduledContentQuery,
  useInfiniteGetNextNPublishedScheduledContentQuery
} from 'data/generated/graphql';
import {
  NUMBER_OF_PREVIOUS_PUBLISHED_CONTENT_TO_SHOW,
  NUMBER_OF_PUBLISHED_CONTENT_TIMESTAMPS_TO_SHOW
} from 'utils/constants';
import { HistoryContent } from '../types';

interface UsePublishHistoryQueriesParams<T extends HistoryContent> {
  liveVersion: T | undefined;
  currentProperty: string | null | undefined;
  windowTarget: number | null;
  windowOlderAnchor: number | null;
  windowNewerAnchor: number | null;
  initialPageSize?: number;
}

export const usePublishHistoryQueries = <T extends HistoryContent>({
  liveVersion,
  currentProperty,
  windowTarget,
  windowOlderAnchor,
  windowNewerAnchor,
  initialPageSize = NUMBER_OF_PREVIOUS_PUBLISHED_CONTENT_TO_SHOW
}: UsePublishHistoryQueriesParams<T>) => {
  const allessehId = liveVersion?.metadata.allessehId ?? '';
  const liveUtc = liveVersion?.metadata.publishUtc ?? 0;

  const {
    data: scheduledContents,
    isLoading: isLoadingScheduledContent,
    refetch: refetchScheduledContent,
    fetchStatus: scheduledContentFetchStatus,
    isFetching: isFetchingScheduledContent
  } = useGetScheduledContentByAllessehIdQuery(
    {
      allessehId,
      publicationKey: currentProperty ?? ''
    },
    { enabled: !!liveVersion }
  );

  const {
    data: lastNScheduledContentsData,
    fetchNextPage: fetchNextPublishedPage,
    hasNextPage: hasNextPublishedPage,
    isLoading: isLoadingLastNScheduledContent,
    fetchStatus: lastNScheduledContentFetchStatus,
    isFetching: isFetchingLastNScheduledContent
  } = useInfiniteGetLastNPublishedScheduledContentQuery(
    {
      allessehId,
      publishUtc: liveUtc,
      status: ScheduledContentStatus.Published,
      paginationParams: {
        first: initialPageSize,
        after: null,
        before: null
      }
    },
    {
      enabled: !!liveVersion,
      getNextPageParam: (lastPage) => {
        const { hasNextPage, endCursor } = lastPage.lastNPublishedScheduledContent.pageInfo;
        if (!hasNextPage || !endCursor) return false;
        return {
          paginationParams: {
            first: initialPageSize,
            after: endCursor,
            before: null
          }
        };
      }
    }
  );

  const { data: publishedTimestampsData } = useGetPublishedScheduledContentTimestampsQuery(
    {
      allessehId,
      publishUtc: liveUtc,
      status: ScheduledContentStatus.Published,
      n: NUMBER_OF_PUBLISHED_CONTENT_TIMESTAMPS_TO_SHOW
    },
    { enabled: !!liveVersion }
  );

  const publishedTimestamps = publishedTimestampsData?.lastNPublishedScheduledContentTimestamps ?? [];

  const { data: windowData, isFetching: isFetchingWindow } = useGetPublishedScheduledContentWindowQuery(
    {
      allessehId,
      targetPublishUtc: windowTarget ?? 0,
      livePublishUtc: liveUtc,
      status: ScheduledContentStatus.Published
    },
    { enabled: !!windowTarget }
  );

  const {
    data: lastNFromWindowData,
    fetchNextPage: fetchOlderFromWindow,
    hasNextPage: hasOlderFromWindow,
    isFetching: isFetchingOlderFromWindow
  } = useInfiniteGetLastNPublishedScheduledContentQuery(
    {
      allessehId,
      publishUtc: windowOlderAnchor ?? 0,
      status: ScheduledContentStatus.Published,
      paginationParams: {
        first: initialPageSize,
        after: null,
        before: null
      }
    },
    {
      enabled: !!liveVersion && windowOlderAnchor != null,
      getNextPageParam: (lastPage) => {
        const { hasNextPage, endCursor } = lastPage.lastNPublishedScheduledContent.pageInfo;
        if (!hasNextPage || !endCursor) return false;
        return {
          paginationParams: {
            first: initialPageSize,
            after: endCursor,
            before: null
          }
        };
      }
    }
  );

  const {
    data: nextNFromWindowData,
    fetchNextPage: fetchNewerFromWindow,
    hasNextPage: hasNewerFromWindow,
    isFetching: isFetchingNewerFromWindow
  } = useInfiniteGetNextNPublishedScheduledContentQuery(
    {
      allessehId,
      fromPublishUtc: windowNewerAnchor ?? 0,
      livePublishUtc: liveUtc,
      status: ScheduledContentStatus.Published,
      paginationParams: {
        first: initialPageSize,
        after: null,
        before: null
      }
    },
    {
      enabled: !!liveVersion && windowNewerAnchor != null,
      getNextPageParam: (lastPage) => {
        const { hasNextPage, endCursor } = lastPage.nextNPublishedScheduledContent.pageInfo;
        if (!hasNextPage || !endCursor) return false;
        return {
          paginationParams: {
            first: initialPageSize,
            after: endCursor,
            before: null
          }
        };
      }
    }
  );

  return {
    scheduledContents,
    isLoadingScheduledContent,
    refetchScheduledContent,
    scheduledContentFetchStatus,
    isFetchingScheduledContent,

    lastNScheduledContentsData,
    fetchNextPublishedPage,
    hasNextPublishedPage,
    isLoadingLastNScheduledContent,
    lastNScheduledContentFetchStatus,
    isFetchingLastNScheduledContent,

    lastNFromWindowData,
    fetchOlderFromWindow,
    hasOlderFromWindow,
    isFetchingOlderFromWindow,

    nextNFromWindowData,
    fetchNewerFromWindow,
    hasNewerFromWindow,
    isFetchingNewerFromWindow,

    windowData,
    isFetchingWindow,

    publishedTimestamps
  };
};
